/**
 * Request an idle callback or fallback to setTimeout
 * @returns {function} The requestIdleCallback function
 */
export const requestIdleCallback =
  typeof window.requestIdleCallback == 'function' ? window.requestIdleCallback : setTimeout;

/**
 * Executes a callback in a separate task after the next frame.
 * Using to defer non-critical tasks until after the interaction is complete.
 * @see https://web.dev/articles/optimize-inp#yield_to_allow_rendering_work_to_occur_sooner
 * @param {() => any} callback - The callback to execute
 */
export const requestYieldCallback = (callback) => {
  requestAnimationFrame(() => {
    setTimeout(callback, 0);
  });
};

/**
 * Tells if we are on a low power device based on the number of CPU cores and RAM
 * @returns {boolean} True if the device is a low power device, false otherwise
 */
export function isLowPowerDevice() {
  return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
}

/**
 * Check if the browser supports View Transitions API
 * @returns {boolean} True if the browser supports View Transitions API, false otherwise
 */
export function supportsViewTransitions() {
  return typeof document.startViewTransition === 'function';
}

/**
 * The current view transition
 * @type {{ current: Promise<void> | undefined }}
 */
export const viewTransition = {
  current: undefined,
};

/**
 * Functions to run when a view transition of a given type is started
 * @type {{ [key: string]: () => Promise<(() => void) | undefined> }}
 */
const viewTransitionTypes = {
  'product-grid': async () => {
    const grid = document.querySelector('.product-grid');
    const productCards = /** @type {HTMLElement[]} */ ([
      ...document.querySelectorAll('.product-grid .product-grid__item'),
    ]);

    if (!grid || !productCards.length) return;

    await new Promise((resolve) =>
      requestIdleCallback(() => {
        const cardsToAnimate = getCardsToAnimate(grid, productCards);

        productCards.forEach((card, index) => {
          if (index < cardsToAnimate) {
            card.style.setProperty('view-transition-name', `product-card-${card.dataset.productId}`);
          } else {
            card.style.setProperty('content-visibility', 'hidden');
          }
        });

        resolve(null);
      })
    );

    return () =>
      productCards.forEach((card) => {
        card.style.removeProperty('view-transition-name');
        card.style.removeProperty('content-visibility');
      });
  },
};

/**
 * Starts a view transition
 * @param {() => void} callback The callback to call when the view transition starts
 * @param {string[]} [types] The types of view transition to use
 * @returns {Promise<void>} A promise that resolves when the view transition finishes
 */
export function startViewTransition(callback, types) {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve) => {
    // Check if View Transitions API is supported
    if (supportsViewTransitions() && !prefersReducedMotion()) {
      let cleanupFunctions = [];

      if (types) {
        for (const type of types) {
          if (viewTransitionTypes[type]) {
            const cleanupFunction = await viewTransitionTypes[type]();
            if (cleanupFunction) cleanupFunctions.push(cleanupFunction);
          }
        }
      }

      const transition = document.startViewTransition(callback);

      if (!viewTransition.current) {
        viewTransition.current = transition.finished;
      }

      if (types) types.forEach((type) => transition.types.add(type));

      transition.finished.then(() => {
        viewTransition.current = undefined;
        cleanupFunctions.forEach((cleanupFunction) => cleanupFunction());
        resolve();
      });

      return;
    }

    // Fallback for browsers that don't support this API yet
    callback();
    resolve();
  });
}

/**
 * @typedef {{ [key: string]: string | undefined }} Headers
 */

/**
 * @typedef {Object} FetchConfig
 * @property {string} method
 * @property {Headers} headers
 * @property {string | FormData | undefined} [body]
 */

/**
 * Creates a fetch configuration object
 * @param {string} [type] The type of response to expect
 * @param {Object} [config] The config of the request
 * @param {FetchConfig['body']} [config.body] The body of the request
 * @param {FetchConfig['headers']} [config.headers] The headers of the request
 * @returns {RequestInit} The fetch configuration object
 */
export function fetchConfig(type = 'json', config = {}) {
  /** @type {Headers} */
  const headers = { 'Content-Type': 'application/json', Accept: `application/${type}`, ...config.headers };

  if (type === 'javascript') {
    headers['X-Requested-With'] = 'XMLHttpRequest';
    delete headers['Content-Type'];
  }

  return {
    method: 'POST',
    headers: /** @type {HeadersInit} */ (headers),
    body: config.body,
  };
}

/**
 * Creates a debounced function that delays calling the provided function (fn)
 * until after wait milliseconds have elapsed since the last time
 * the debounced function was invoked. The returned function has a .cancel()
 * method to cancel any pending calls.
 *
 * @template {(...args: any[]) => any} T
 * @param {T} fn The function to debounce
 * @param {number} wait The time (in milliseconds) to wait before calling fn
 * @returns {T & { cancel(): void }} A debounced version of fn with a .cancel() method
 */
export function debounce(fn, wait) {
  /** @type {number | undefined} */
  let timeout;

  /** @param {...any} args */
  function debounced(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  }

  // Add the .cancel method:
  debounced.cancel = () => {
    clearTimeout(timeout);
  };

  return /** @type {T & { cancel(): void }} */ (debounced);
}

/**
 * Creates a throttled function that calls the provided function (fn) at most once per every wait milliseconds
 *
 * @template {(...args: any[]) => any} T
 * @param {T} fn The function to throttle
 * @param {number} delay The time (in milliseconds) to wait before calling fn
 * @returns {T & { cancel(): void }} A throttled version of fn with a .cancel() method
 */
export function throttle(fn, delay) {
  let lastCall = 0;

  /** @param {...any} args */
  function throttled(...args) {
    const now = performance.now();
    // If the time since the last call exceeds the delay, execute the callback
    if (now - lastCall >= delay) {
      lastCall = now;
      fn.apply(this, args);
    }
  }

  throttled.cancel = () => {
    lastCall = performance.now();
  };

  return /** @type {T & { cancel(): void }} */ (throttled);
}

/**
 * A media query for reduced motion
 * @type {MediaQueryList}
 */
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Check if the user prefers reduced motion
 * @returns {boolean} True if the user prefers reduced motion, false otherwise
 */
export function prefersReducedMotion() {
  return reducedMotion.matches;
}

/**
 * Normalize a string
 * @param {string} str The string to normalize
 * @returns {string} The normalized string
 */
export function normalizeString(str) {
  return str
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/**
 * Format a money value
 * @param {string} value The value to format
 * @returns {string} The formatted value
 */
export function formatMoney(value) {
  let valueWithNoSpaces = value.replace(' ', '');
  if (valueWithNoSpaces.indexOf(',') === -1) return valueWithNoSpaces;
  if (valueWithNoSpaces.indexOf(',') < valueWithNoSpaces.indexOf('.')) return valueWithNoSpaces.replace(',', '');
  if (valueWithNoSpaces.indexOf('.') < valueWithNoSpaces.indexOf(','))
    return valueWithNoSpaces.replace('.', '').replace(',', '.');
  if (valueWithNoSpaces.indexOf(',') !== -1) return valueWithNoSpaces.replace(',', '.');

  return valueWithNoSpaces;
}

/**
 * Check if the document is ready/loaded and call the callback when it is.
 * @param {() => void} callback The function to call when the document is ready.
 */
export function onDocumentLoaded(callback) {
  if (document.readyState === 'complete') {
    callback();
  } else {
    window.addEventListener('load', callback);
  }
}

/**
 * Check if the DOM is ready and call the callback when it is.
 * This fires when the DOM is fully parsed but before all resources are loaded.
 * @param {() => void} callback The function to call when the DOM is ready.
 */
export function onDocumentReady(callback) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback);
  } else {
    callback();
  }
}

/**
 * Wait for all animations to finish before calling the callback.
 * @param {Element | Element[]} elements The element(s) whose animations to wait for.
 * @param {() => void} [callback] The function to call when all animations are finished.
 * @param {Object} [options] The options to pass to `Element.getAnimations`.
 * @returns {Promise<void>} A promise that resolves when all animations are finished.
 */
export function onAnimationEnd(elements, callback, options = { subtree: true }) {
  const animations = Array.isArray(elements)
    ? elements.flatMap((element) => element.getAnimations(options))
    : elements.getAnimations(options);
  const animationPromises = animations.reduce((acc, animation) => {
    // Ignore ViewTimeline animations
    if (animation.timeline instanceof DocumentTimeline) {
      acc.push(animation.finished);
    }

    return acc;
  }, /** @type {Promise<Animation>[]} */ ([]));

  return Promise.allSettled(animationPromises).then(callback);
}

/**
 * Check if the click is outside the element.
 * @param {MouseEvent} event The mouse event.
 * @param {Element} element The element to check.
 * @returns {boolean} True if the click is outside the element, false otherwise.
 */
export function isClickedOutside(event, element) {
  if (event.target instanceof HTMLDialogElement || !(event.target instanceof Element)) {
    return !isPointWithinElement(event.clientX, event.clientY, element);
  }

  return !element.contains(event.target);
}

/**
 * Check if a point is within an element.
 * @param {number} x The x coordinate of the point.
 * @param {number} y The y coordinate of the point.
 * @param {Element} element The element to check.
 * @returns {boolean} True if the point is within the element, false otherwise.
 */
export function isPointWithinElement(x, y, element) {
  const { left, right, top, bottom } = element.getBoundingClientRect();

  return x >= left && x <= right && y >= top && y <= bottom;
}

/**
 * A media query for large screens
 * @type {MediaQueryList}
 */
export const mediaQueryLarge = matchMedia('(min-width: 750px)');

/**
 * Check if the current breakpoint is mobile
 * @returns {boolean} True if the current breakpoint is mobile, false otherwise
 */
export function isMobileBreakpoint() {
  return !mediaQueryLarge.matches;
}

/**
 * Check if the current breakpoint is desktop
 * @returns {boolean} True if the current breakpoint is desktop, false otherwise
 */
export function isDesktopBreakpoint() {
  return mediaQueryLarge.matches;
}

/**
 * Clamps a number between a minimum and maximum value.
 * @param {number} value - The input number to clamp.
 * @param {number} min - The minimum value.
 * @param {number} max - The maximum value.
 * @returns {number} The clamped value.
 */
export function clamp(value, min, max) {
  return Math.max(min, Math.min(value, max));
}

/**
 * Calculates the center point of an element along the specified axis.
 * @param {Element} element - The DOM element to find the center of.
 * @param {'x' | 'y'} [axis] - The axis ('x' or 'y') to get the center for. If not provided, returns both axes.
 * @template {('x' | 'y')} T
 * @param {T} [axis]
 * @returns {T extends ('x' | 'y') ? number : {x: number, y: number}} The center point along the axis or an object with x and y coordinates.
 */
export function center(element, axis) {
  const { left, width, top, height } = element.getBoundingClientRect();
  const point = {
    x: left + width / 2,
    y: top + height / 2,
  };

  if (axis) return /**  @type {any} */ (point[axis]);

  return /**  @type {any} */ (point);
}

/**
 * Calculates the start point of an element along the specified axis.
 * @param {Element} element - The DOM element to find the start of.
 * @param {'x' | 'y'} [axis] - The axis ('x' or 'y') to get the start for. If not provided, returns both axes.
 * @returns {number | {x: number, y: number}} The start point along the axis or an object with x and y coordinates.
 */
export function start(element, axis) {
  const { left, top } = element.getBoundingClientRect();
  const point = { x: left, y: top };

  if (axis) return /**  @type {any} */ (point[axis]);

  return /**  @type {any} */ (point);
}

/**
 * Finds the value in an array that is closest to a target value.
 * @param {number[]} values - An array of numbers.
 * @param {number} target - The target number to find the closest value to.
 * @returns {number} The value from the array closest to the target.
 */
export function closest(values, target) {
  return values.reduce(function (prev, curr) {
    return Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev;
  });
}

/**
 * Prevents the default action of an event.
 * @param {Event} event - The event to prevent the default action of.
 */
export function preventDefault(event) {
  event.preventDefault();
}

/**
 * Get the visible elements within a root element.
 * @template {Element} T
 * @param {Element} root - The element within which elements should be visible.
 * @param {T[] | undefined} elements - The elements to check for visibility.
 * @param {number} [ratio=1] - The minimum percentage of the element that must be visible.
 * @param {'x' | 'y'} [axis] - Whether to only check along 'x' axis, 'y' axis, or both if undefined.
 * @returns {T[]} An array containing the visible elements.
 */
export function getVisibleElements(root, elements, ratio = 1, axis) {
  if (!elements?.length) return [];
  const rootRect = root.getBoundingClientRect();

  return elements.filter((element) => {
    const { width, height, top, right, left, bottom } = element.getBoundingClientRect();

    if (ratio < 1) {
      const intersectionLeft = Math.max(rootRect.left, left);
      const intersectionRight = Math.min(rootRect.right, right);
      const intersectionWidth = Math.max(0, intersectionRight - intersectionLeft);

      if (axis === 'x') {
        return width > 0 && intersectionWidth / width >= ratio;
      }

      const intersectionTop = Math.max(rootRect.top, top);
      const intersectionBottom = Math.min(rootRect.bottom, bottom);
      const intersectionHeight = Math.max(0, intersectionBottom - intersectionTop);

      if (axis === 'y') {
        return height > 0 && intersectionHeight / height >= ratio;
      }

      const intersectionArea = intersectionWidth * intersectionHeight;
      const elementArea = width * height;

      // Check that at least the specified ratio of the element is visible
      return elementArea > 0 && intersectionArea / elementArea >= ratio;
    }

    const isWithinX = left >= rootRect.left && right <= rootRect.right;
    if (axis === 'x') {
      return isWithinX;
    }

    const isWithinY = top >= rootRect.top && bottom <= rootRect.bottom;
    if (axis === 'y') {
      return isWithinY;
    }

    return isWithinX && isWithinY;
  });
}

export function getIOSVersion() {
  const { userAgent } = navigator;
  const isIOS = /(iPhone|iPad)/i.test(userAgent);

  if (!isIOS) return null;

  const version = userAgent.match(/OS ([\d_]+)/)?.[1];
  const [major, minor] = version?.split('_') || [];
  if (!version || !major) return null;

  return {
    fullString: version.replace('_', '.'),
    major: parseInt(major, 10),
    minor: minor ? parseInt(minor, 10) : 0,
  };
}

/**
 * Determines which grid items should be animated during a transition.
 * It makes an estimation based on the zoom-out card size because it's
 * the common denominator for both transition states. I.e. transitioning either
 * from 10 to 20 cards the other way around, both need 20 cards to be animated.
 * @param {Element} grid - The grid element
 * @param {Element[]} cards - The cards to animate
 * @returns {number} - Number of cards that should be animated
 */
function getCardsToAnimate(grid, cards) {
  if (!grid || !cards || cards.length === 0) return 0;

  const itemSample = cards[0];
  if (!itemSample) return 0;

  // Calculate the visible area of the grid for the Y axis. Assume X is always fully visible:
  const gridRect = grid.getBoundingClientRect();
  const visibleArea = {
    top: Math.max(0, gridRect.top),
    bottom: Math.min(window.innerHeight, gridRect.bottom),
  };

  const visibleHeight = Math.round(visibleArea.bottom - visibleArea.top);
  if (visibleHeight <= 0) return 0;

  /** @type {import('product-card').ProductCard | null} */
  const cardSample = itemSample.querySelector('product-card');
  const gridStyle = getComputedStyle(grid);

  const galleryAspectRatio = cardSample?.refs?.cardGallery?.style.getPropertyValue('--gallery-aspect-ratio') || '';
  let aspectRatio = parseFloat(galleryAspectRatio) || 0.5;
  if (galleryAspectRatio?.includes('/')) {
    const [width = '1', height = '2'] = galleryAspectRatio.split('/');
    aspectRatio = parseInt(width, 10) / parseInt(height, 10);
  }

  const cardGap = parseInt(cardSample?.refs?.productCardLink?.style.getPropertyValue('--product-card-gap') || '') || 12;
  const gridGap = parseInt(gridStyle.getPropertyValue('--product-grid-gap')) || 12;

  // Assume only a couple of lines of text in the card details (title and price).
  // If the title wraps into more lines, we might just animate more cards, but that's fine.
  const detailsSize = ((parseInt(gridStyle.fontSize) || 16) + 2) * 2;

  const isMobile = window.innerWidth < 750;

  // Always use the zoom-out state card width
  const cardWidth = isMobile ? Math.round((gridRect.width - gridGap) / 2) : 100;
  const cardHeight = Math.round(cardWidth / aspectRatio) + cardGap + detailsSize;

  // Calculate the number of cards that fit in the visible area:
  // - The width estimation is pretty accurate, we can ignore decimals.
  // - The height estimation needs to account for peeking rows, so we round up.
  const columnsInGrid = isMobile ? 2 : Math.floor((gridRect.width + gridGap) / (cardWidth + gridGap));
  const rowsInGrid = Math.ceil((visibleHeight - gridGap) / (cardHeight + gridGap));

  return columnsInGrid * rowsInGrid;
}

/**
 * Preloads an image
 * @param {string} src - The source of the image to preload
 */
export function preloadImage(src) {
  const image = new Image();
  image.src = src;
}

export class TextComponent extends HTMLElement {
  shimmer() {
    this.setAttribute('shimmer', '');
  }
}

if (!customElements.get('text-component')) {
  customElements.define('text-component', TextComponent);
}

/**
 * Resets the shimmer attribute on all elements in the container.
 * @param {Element} [container] - The container to reset the shimmer attribute on.
 */
export function resetShimmer(container = document.body) {
  const shimmer = container.querySelectorAll('[shimmer]');
  shimmer.forEach((item) => item.removeAttribute('shimmer'));
}

/**
 * Change the meta theme color of the header.
 * @param {Element} colorSourceElement - The HTML element whose background-color will determine the new theme-color.
 */
export function changeMetaThemeColor(colorSourceElement) {
  const metaThemeColor = document.head.querySelector('meta[name="theme-color"]');
  const containerStyle = window.getComputedStyle(colorSourceElement);
  if (metaThemeColor) metaThemeColor.setAttribute('content', containerStyle.backgroundColor);
}

class Scheduler {
  /** @type {Set<() => void>} */
  #queue = new Set();
  /** @type {boolean} */
  #scheduled = false;

  /** @param {() => void} task */
  schedule = async (task) => {
    this.#queue.add(task);

    if (!this.#scheduled) {
      this.#scheduled = true;

      // Wait for any in-progress view transitions to finish
      if (viewTransition.current) await viewTransition.current;

      requestAnimationFrame(this.flush);
    }
  };

  flush = () => {
    for (const task of this.#queue) {
      task();
    }

    this.#queue.clear();
    this.#scheduled = false;
  };
}

export const scheduler = new Scheduler();

Theme.utilities = {
  ...Theme.utilities,
  scheduler: scheduler,
};



document.addEventListener("DOMContentLoaded", () => {
  // === Progress bar updater ===
  const updateProgressBar = () => {
    const target = document.querySelector(".add-to-cart-total-price");
    const line = document.querySelector(".divider__line");
    const circle = document.querySelector(".divider__circle");
    const FREE_SHIPPING_THRESHOLD = document.querySelector(".divider-container").dataset.discountMinimum;
    const shippingText = document.querySelector(".text-below");
    const subscriptionOption = document.querySelector(".appstle_subscription_wrapper_option.appstle_include_dropdown");

    if (!target || !line || !circle || !subscriptionOption) return;

    const isSubscriptionActive = subscriptionOption.classList.contains("appstle-active-option");

    if (isSubscriptionActive) {
      // Subscription → full bar
      const textsEl = document.querySelector('.info-below');
      circle.style.transform = `translateX(${line.offsetWidth}px)`;
      shippingText.innerText = textsEl.dataset.discountAppliedText;
    } else {
      // One-time → proportional progress

      const text = target.innerText;
      const match = text.match(/\d+([,.]\d+)?/);
      const number = match ? parseFloat(match[0].replace(",", ".")) : 0;
      const progress = Math.min(number / FREE_SHIPPING_THRESHOLD, 1);
      const circleOffset = progress * line.offsetWidth;
      let trackerValue = document.querySelector(".tracker").dataset.value

      if (trackerValue > 1) {
        document.querySelector("#discount-announcement").style.display = "flex";
      } else {
        document.querySelector("#discount-announcement").style.display = "none";
      }

      if (trackerValue) {
        trackerValue = trackerValue * circleOffset;
      }
      const textsEl = document.querySelector('.info-below');
      circle.style.transform = `translateX(${circleOffset}px)`;
      if (number >= FREE_SHIPPING_THRESHOLD) {
        shippingText.innerText = textsEl.dataset.discountAppliedText;
      } else {
        shippingText.innerText = textsEl.dataset.discountPendingText;
      }
    }
  };

  setTimeout(function () {
    const refreshForm = () => {
      const form = document.querySelector(".shopify-product-form");
      const oneTime = form.querySelector(".appstle_subscription_wrapper_option");
      const subscription = form.querySelector(
        ".appstle_subscription_wrapper_option.appstle_include_dropdown .appstle_subscription_radio_wrapper"
      );
      let total = document.querySelector(".add-to-cart-total-price");
      const notice = document.querySelector("#shopify-buyer-consent");

      let qtyComponent = document.querySelector("quantity-selector-component.quantity-selector");
      let input = qtyComponent.querySelector("input");
      input.value = 1;
      

      // set text
      const textsEl = document.getElementById('custom-texts');
      document.querySelector(".appstle_one_time_text").innerText = textsEl.dataset.oneTimePrimaryText;
      document.querySelector(".appstle_subscribe_save_text").innerText = textsEl.dataset.subscriptionPrimaryText;
      
      document.querySelector(".appstle_select_label").innerText = textsEl.dataset.subscriptionFrequencyText;
      document.querySelector(".appstle_widget_title").innerText = textsEl.dataset.title;
      
      // === Add extra text ===
      const boxes = document.querySelectorAll(".appstle_subscription_wrapper_option");
      const subnote = document.querySelector(".subnote")

      if (subnote == null) {
        boxes.forEach((box) => {
          if (box.classList.contains("appstle_include_dropdown")) {
            const note = `
            <div class='subnote'>
              <p class='buy-option-note-bold'>${textsEl.dataset.subscriptionSecondaryText}</p>
              <p class='buy-option-subnote'>${textsEl.dataset.subscriptionTertiaryText}</p>
            </div>`;
            box.querySelector(".appstle_subscription_radio_wrapper").insertAdjacentHTML("afterend", note);
          } else {
            box.insertAdjacentHTML(
              "beforeend",
              `<p class='buy-option-note'>${textsEl.dataset.oneTimeSecondaryText}</p>`
            );
          }
        });
      }

      // === Observer for automatic updates ===
      const target = document.querySelector(".add-to-cart-total-price");
      const observer = new MutationObserver(() => updateProgressBar());
      observer.observe(target, { characterData: true, childList: true, subtree: true });

      document.querySelector("#appstle_selling_plan0").addEventListener("change", () => {
        document.querySelector(".appstle_subscribe_save_text").innerText = textsEl.dataset.subscriptionPrimaryText;
      })

      document.querySelector(".quantity-selector").addEventListener("click", () => {
        const textsEl = document.getElementById('custom-texts');
        setTimeout(() => {
          document.querySelector(".appstle_subscribe_save_text").innerText = textsEl.dataset.subscriptionPrimaryText;
        }, 300); // 50–150ms usually enough
      });


      // const appstleTextEl = document.querySelector(".appstle_subscribe_save_text");
      // if (appstleTextEl) {
      //   const observer = new MutationObserver(() => {
      //     if (appstleTextEl.innerText !== textsEl.dataset.subscriptionPrimaryText) {
      //       appstleTextEl.innerText = textsEl.dataset.subscriptionPrimaryText;
      //     }
      //   });
      //   observer.observe(appstleTextEl, { childList: true, characterData: true, subtree: true });
      // }
                  

      // === Click Handlers ===
      oneTime.addEventListener("click", () => {
        // Wait until Appstle finishes toggling classes
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            total.innerText = oneTime.querySelector(".appstle_subscription_amount").innerText;
            oneTime.style.backgroundColor = "#FFDBDA";
            subscription.parentNode.style.backgroundColor = "transparent";
            let qtyComponent = document.querySelector("quantity-selector-component.quantity-selector");
            let input = qtyComponent.querySelector("input");
            input.value = 1;
            updateProgressBar();
            document.querySelector(".appstle_subscribe_save_text").innerText = textsEl.dataset.subscriptionPrimaryText;
          });
        });
      });

      subscription.addEventListener("click", () => {
        // Move circle instantly to full bar to avoid flicker
        const line = document.querySelector(".divider__line");
        const circle = document.querySelector(".divider__circle");
        circle.style.transform = `translateX(${line.offsetWidth}px)`;

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            total.innerText = subscription.querySelector(".appstle_subscription_amount").innerText;
            subscription.parentNode.style.backgroundColor = "#FFDBDA";
            oneTime.style.backgroundColor = "transparent";
            notice.style.display = "block";
            let qtyComponent = document.querySelector("quantity-selector-component.quantity-selector");
            let input = qtyComponent.querySelector("input");
            input.value = 1;
            updateProgressBar();
            document.querySelector(".appstle_subscribe_save_text").innerText = textsEl.dataset.subscriptionPrimaryText;
          });
        });
      });
    };

    const variantSelect = document.querySelector("#Option-0");
    let options = document.querySelectorAll(".appstle_subscription_wrapper_option");

    variantSelect.addEventListener("change", () => {
      options = document.querySelectorAll(".appstle_subscription_wrapper_option");
      options.forEach((option) => {
        if (option.classList.contains("appstle-active-option")) {
          total.innerText = option.querySelector(".appstle_subscription_amount").innerText;
        }
      });
      refreshForm();
    });

    const form = document.querySelector(".shopify-product-form");
    let total = document.querySelector(".add-to-cart-total-price");
    const subscription = form.querySelector(".appstle-active-option");
    total.innerText = subscription.querySelector(".appstle_subscription_amount").innerText;
    refreshForm();

    // === Initial progress position on load ===
    updateProgressBar();

    const buyBtn = document.querySelector("add-to-cart-component button");
    const textsEl = document.getElementById('custom-texts');
    buyBtn.addEventListener("click", () => {
      const prevText = buyBtn.innerHTML;
      buyBtn.innerHTML = `<span class="add-to-cart-text"><span class="add-to-cart-text__content">${textsEl.dataset.added}</span></span>`
      setTimeout(() => {
        buyBtn.innerHTML = prevText;
      }, 2000)
    })

    document.querySelector(".cart-drawer__header button").addEventListener("click", () => {
      refreshForm();
      updateProgressBar();
    })

  }, 1500);

});



function initReveal() {
  const targets = document.querySelectorAll(
    '.product-subinfo-section'
  );

  if (!targets.length) return;

  // Ensure all start hidden
  targets.forEach((el) => {
    el.classList.add('reveal-element');
  });

  // Create observer (more generous margins for mobile)
  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Reveal each element in sequence
          targets.forEach((el, i) => {
            el.style.transitionDelay = `${i * 400}ms`;
            el.classList.add('visible');
          });
          obs.disconnect(); // only trigger once
        }
      });
    },
    {
      threshold: 0.05,
      rootMargin: '0px 0px -20% 0px', // triggers earlier on small screens
    }
  );

  // Watch the first visible target
  if (targets[0]) observer.observe(targets[0]);
}

// Wait for everything to load, including Shopify dynamic sections
document.addEventListener('DOMContentLoaded', () => {
  initReveal();
  setTimeout(initReveal, 300);
  setTimeout(initReveal, 1000);
});

// Fallback for very old browsers (optional)
if (!('IntersectionObserver' in window)) {
  window.addEventListener('scroll', () => {
    document
      .querySelectorAll('.reveal-element')
      .forEach((el) => el.classList.add('visible'));
  });
}


document.addEventListener('DOMContentLoaded', () => {
  customElements.whenDefined('cart-drawer-component').then(() => {
    const addToCartButton = document.querySelector('[on\\:click="/handleClick;"]');

    if (addToCartButton) {
      addToCartButton.addEventListener('click', () => {
        const cartDrawer = document.querySelector('cart-drawer-component');
        setTimeout(() => {
          cartDrawer.open();
        }, 1000)
      });
    }
  });
});



document.addEventListener("DOMContentLoaded", () => {
  const container = document.querySelector(".product-subinfo-images");
  let images = Array.from(container.querySelectorAll("img"));

  // 1. Assign rotation classes based on original position
  images.forEach((img, i) => {
    // Even index → positive tilt, Odd index → negative tilt
    img.classList.add(i % 2 === 0 ? "tilt-pos" : "tilt-neg");
  });

  // 2. Clone all images for seamless looping, keeping the same classes
  images.forEach(img => {
    const clone = img.cloneNode(true);
    container.appendChild(clone);
  });

  // After cloning, we need to recalc the image list
  images = Array.from(container.querySelectorAll("img"));

  // Compute width of ONE item including the gap
  const style = window.getComputedStyle(container);
  const gap = parseFloat(style.columnGap || style.gap) || 0;
  const itemWidth = images[0].offsetWidth + gap;

  const originalCount = images.length / 2; // how many originals
  const originalWidth = itemWidth * originalCount;

  let index = 0;
  const interval = 2500; // every 2.5 seconds

  function autoScroll() {
    index++;

    container.scrollTo({
      left: index * itemWidth,
      behavior: "smooth"
    });

    if (container.scrollLeft >= originalWidth) {
      index = 0;

      // jump back instantly
      container.scrollTo({
        left: 0,
        behavior: "auto"
      });

      // trigger the next movement immediately
      setTimeout(autoScroll, 50);
      return;
    }
  }


  setInterval(autoScroll, interval);
});

document.addEventListener("DOMContentLoaded", () => {
  const container = document.querySelector(".testimonials");
  let images = Array.from(container.querySelectorAll(".image-card"));

  // 1. Assign rotation classes based on original position
  images.forEach((img, i) => {
    // Even index → positive tilt, Odd index → negative tilt
    img.classList.add(i % 2 === 0 ? "tilt-pos" : "tilt-neg");
  });

  // 2. Clone all images for seamless looping, keeping the same classes
  images.forEach(img => {
    const clone = img.cloneNode(true);
    container.appendChild(clone);
  });

  // After cloning, we need to recalc the image list
  images = Array.from(container.querySelectorAll(".image-card"));

  // Compute width of ONE item including the gap
  const style = window.getComputedStyle(container);
  const gap = parseFloat(style.columnGap || style.gap) || 0;
  const itemWidth = images[0].offsetWidth + gap;

  const originalCount = images.length / 2; // how many originals
  const originalWidth = itemWidth * originalCount;

  let index = 0;
  const interval = 2500; // every 2.5 seconds

  function autoScroll() {
    index++;

    container.scrollTo({
      left: index * itemWidth,
      behavior: "smooth"
    });

    if (container.scrollLeft >= originalWidth) {
      index = 0;

      // jump back instantly
      container.scrollTo({
        left: 0,
        behavior: "auto"
      });

      // trigger the next movement immediately
      setTimeout(autoScroll, 50);
      return;
    }
  }


  setInterval(autoScroll, interval);
});


document.addEventListener('DOMContentLoaded', () => {
  const form = document.querySelector('add-to-cart-component').closest('form');

  if (!form) return;

  form.addEventListener('submit', (event) => {
    // Log info
    const variantId = form.querySelector('[name="id"]')?.value;
    const quantity = form.querySelector('[name="quantity"]')?.value;
    const selectedPlan = form.querySelector('input[name="selling_plan"]:checked')?.value || 'one-time';

    // Let Shopify handle the submission normally
  });

  // Example: trigger submit programmatically
  const addToCartButton = form.querySelector('button[type="submit"]');
  addToCartButton.addEventListener('click', (e) => {
    e.preventDefault(); // prevent default for logging first
    form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  });
});



document.addEventListener("DOMContentLoaded", () => {
  const desktopVideo = document.querySelector('.desktop-hero-video');

  if (desktopVideo) {
    desktopVideo.addEventListener('ended', () => {
      desktopVideo.pause();
      desktopVideo.currentTime = vid.duration;
    });
  }


  const marquee = document.querySelectorAll('.marquee__wrapper')[1];

  if (marquee) {
    setTimeout(() => {
      function getLockDistance() {
        const rect = marquee.getBoundingClientRect();
        const fromTop = rect.top + window.scrollY;
        const pageHeight = document.body.scrollHeight;
        return pageHeight - fromTop;
      }

      let lockDistance = getLockDistance();
      const button = document.querySelector('.floating-button');

      window.addEventListener('scroll', () => {
        const scrollPos = window.scrollY + window.innerHeight;
        const pageHeight = document.body.scrollHeight;
        const distanceFromBottom = pageHeight - scrollPos;

        if (distanceFromBottom <= lockDistance) {
          button.classList.add('stuck');
        } else {
          button.classList.remove('stuck');
        }
      });
    }, 3000); // adjust if needed
  }

  const emailSignUpForm = document.querySelector(".email-signup__form");
  const checkbox = document.querySelector(".footer-checkbox input");
  const submitBtnContainer = emailSignUpForm.querySelector(".email-btn-container")
  const submitBtn = emailSignUpForm.querySelector(".email-btn");

  if (emailSignUpForm && checkbox) {
    checkbox.addEventListener("change", () => {
      if (checkbox.checked) {
        submitBtn.disabled = false;
        if (!politicaError.classList.contains("error-hidden")) {
          politicaError.classList.add("error-hidden");
        }
      } else {
        if (submitBtn) {
          submitBtn.disabled = true;
        }
      }
    })

    submitBtnContainer.addEventListener('click', () => {
      if (!checkbox.checked) {
        submitBtn.disabled = true;
        politicaError.classList.remove("error-hidden");
      } else {
        submitBtn.disabled = false;
        emailSignUpForm.submit();
      }
    })
  }

  const contactForm = document.querySelector(".contact-form__form");
  const greenBtnContainer = document.querySelector(".contact-page-btn-container");
  const checkboxGreen = document.querySelector(".checkbox-flex-green input");
  const politicaError = document.querySelector(".error-footer");
  const politicaErrorGreen = document.querySelector(".error-green");
  const greenBtn = greenBtnContainer.querySelector("button");

  setTimeout(() => {
    if (greenBtnContainer) {
      greenBtn.disabled = true;
    }
  }, 1000);


  if (contactForm) {
    greenBtnContainer.addEventListener("click", () => {
      if (!checkboxGreen.checked) {
        greenBtnContainer.querySelector(".contact-page-btn-container").disabled = true;
        if (politicaErrorGreen.classList.contains("error-hidden-green")) {
          politicaErrorGreen.classList.remove("error-hidden-green");
        }
      } else {
        contactForm.submit();
      }
    })
  }

  if (checkboxGreen) {
    checkboxGreen.addEventListener("change", () => {
      if (checkboxGreen.checked) {
        greenBtn.disabled = false;
        if (!politicaError.classList.contains("error-hidden-green")) {
          politicaError.classList.add("error-hidden-green");
        }
      } else {
        greenBtn.disabled = true;
      }
    })
  }
})


  const popupModal = document.querySelector(".popup-modal");
  
  if (popupModal) {
    const popupForm = popupModal.querySelector(".contact-form");
    const popupCheckbox = popupModal.querySelector(".checkbox-flex-popup input");
    const popupError = popupModal.querySelector(".error-red");
    const popupSubmit = popupModal.querySelector(".contact-form button");
    const popupBtnContainer = popupModal.querySelector(".popup-btn-container");

    setTimeout(() => {
      popupBtnContainer.addEventListener("click", () => {
        if (!popupCheckbox.checked) {
          popupSubmit.disabled = true;
          popupError.classList.remove("error-hidden-red");
        } else {
          popupForm.submit();
        }
      })
      popupCheckbox.addEventListener("change", () => {
        if (popupCheckbox.checked) {
          popupSubmit.disabled = false;
          popupError.classList.add("error-hidden-red");
        }
      })
    }, 1000);
  }